import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { rolesAPI } from '../../roles/services/roles';
import { staffAPI } from '../../staff/services/staff';
import { roomsAPI } from '../../rooms/services/rooms';
import { equipmentAPI } from '../../equipment/services/equipment';
import { tasksAPI } from '../../tasks/services/tasks';
import { useAuth } from '../../../context/AuthContext';

export function useOnboardingProgress() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stepData, setStepData] = useState({
    roles: [],
    staff: [],
    rooms: [],
    equipment: [],
    tasks: [],
  });

  const refresh = useCallback(async () => {
    if (!user?.department?.department_id) return;
    setLoading(true);
    try {
      const [rolesRes, staffRes, roomsRes, equipRes, tasksRes] = await Promise.all([
        rolesAPI.getDepartmentRoles(),
        staffAPI.getDepartmentStaff(),
        roomsAPI.listRoomsByDepartment(user.department.department_id),
        equipmentAPI.listEquipmentByDepartment(),
        tasksAPI.getDepartmentTasks({ limit: 1 }),
      ]);
      setStepData({
        roles: rolesRes.data?.roles || [],
        staff: staffRes.data?.staff_members || [],
        rooms: roomsRes.data?.rooms || [],
        equipment: equipRes.data?.equipment || [],
        tasks: tasksRes.data?.data?.tasks || [],
      });
    } catch {
      // silent — progress stays stale until next refresh
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const steps = [
    {
      id: 'roles_permissions',
      label: t('onboarding:steps.roles_permissions'),
      completed:
        stepData.roles.length > 0 &&
        stepData.roles.some(r => r.permissions_count > 0),
    },
    {
      id: 'staff_assignments',
      label: t('onboarding:steps.staff_assignments'),
      completed:
        stepData.staff.length > 0 &&
        stepData.roles.some(r => r.staff_count > 0),
    },
    {
      id: 'rooms',
      label: t('onboarding:steps.rooms'),
      completed: stepData.rooms.length > 0,
    },
    {
      id: 'equipment',
      label: t('onboarding:steps.equipment'),
      completed: stepData.equipment.length > 0,
    },
    {
      id: 'tasks',
      label: t('onboarding:steps.tasks'),
      completed: stepData.tasks.length > 0,
    },
  ];

  return { steps, stepData, loading, refresh };
}
